import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/Loader";

const NDA = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const annonce = location.state?.annonce;
  const { user } = useStore();
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingNDA, setExistingNDA] = useState(null);
  const [checkingNDA, setCheckingNDA] = useState(true);
  const [formData, setFormData] = useState({
    entityType: "morale", // "morale" or "physique"
    companyName: "",
    address: "",
    representativeName: "",
    representativeFunction: "",
    sector: "",
    city: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [accepted, setAccepted] = useState(false);

  const sectorOptions = [
    "AdTech",
    "BigData",
    "BioTech",
    "CleanTech",
    "Cybersécurité",
    "Data",
    "DeepTech",
    "FinTech",
    "Alimentation",
    "Santé",
    "RH",
    "IOT",
    "Média",
    "Transport",
    "Voyage",
    "PropTech",
    "Securité",
  ];

  const fetchNDA = async () => {
    if (!user || !annonce) {
      setCheckingNDA(false);
      return;
    }

    try {
      const { ok, data } = await api.post("/nda/search", {
        company_id: annonce._id,
        user_id: user._id,
      });

      if (ok && data && data.length > 0) {
        // Vérifier que l'utilisateur a bien signé le NDA
        const nda = data[0];
        if (nda.nda_user_from_signed === true) {
          setExistingNDA(nda);
        }
      }
    } catch (error) {
      console.error("Error checking existing NDA:", error);
    } finally {
      setCheckingNDA(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        companyName: user.company?.name || "",
        representativeName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        representativeFunction: user.function || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchNDA();
  }, [user, annonce]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrintNDA = async (e) => {
    e.preventDefault();

    if (!accepted) {
      toast.error("Vous devez accepter les termes du NDA");
      return;
    }

    if (formData.entityType === "morale") {
      const missingFields = [];
      if (!formData.companyName) missingFields.push("Raison sociale");
      if (!formData.address) missingFields.push("Adresse");
      if (!formData.representativeName) missingFields.push("Nom du représentant");
      if (!formData.representativeFunction) missingFields.push("Fonction du représentant");
      if (!formData.sector) missingFields.push("Secteur d'activité");
      if (missingFields.length > 0) {
        toast.error(`Veuillez remplir tous les champs obligatoires : ${missingFields.join(", ")}`);
        return;
      }
    } else {
      const missingFields = [];
      if (!formData.address) missingFields.push("Adresse");
      if (!formData.representativeName) missingFields.push("Nom");
      if (missingFields.length > 0) {
        toast.error(`Veuillez remplir tous les champs obligatoires : ${missingFields.join(", ")}`);
        return;
      }
    }
    setPrinting(true);
    try {
      const response = await api.post(`/nda/${annonce._id}/pdf`, {
        formData,
      });
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating NDA:", error);
      toast.error("Erreur lors de la génération du NDA");
    } finally {
      setPrinting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!accepted) {
      toast.error("Vous devez accepter les termes du NDA");
      return;
    }

    if (formData.entityType === "morale") {
      const missingFields = [];
      if (!formData.companyName) missingFields.push("Raison sociale");
      if (!formData.address) missingFields.push("Adresse");
      if (!formData.representativeName) missingFields.push("Nom du représentant");
      if (!formData.representativeFunction) missingFields.push("Fonction du représentant");
      if (!formData.sector) missingFields.push("Secteur d'activité");
      if (missingFields.length > 0) {
        toast.error(`Veuillez remplir tous les champs obligatoires : ${missingFields.join(", ")}`);
        return;
      }
    } else {
      const missingFields = [];
      if (!formData.address) missingFields.push("Adresse");
      if (!formData.representativeName) missingFields.push("Nom");
      if (missingFields.length > 0) {
        toast.error(`Veuillez remplir tous les champs obligatoires : ${missingFields.join(", ")}`);
        return;
      }
    }
    setSigning(true);
    try {
      const { ok, data } = await api.post(`/nda/${annonce._id}/sign`, {
        formData,
      });
      if (!ok) return toast.error("Erreur lors de la signature du NDA");
      toast.success("NDA signé avec succès");
      navigate("/dataroom");
    } catch (error) {
      console.error("Error signing NDA:", error);
      toast.error("Erreur lors de la signature du NDA");
    } finally {
      setSigning(false);
    }
  };

  const isFormValid =
    formData.entityType === "morale"
      ? formData.companyName && formData.address && formData.representativeName && formData.representativeFunction && formData.sector
      : formData.address && formData.representativeName;

  if (loading || checkingNDA) return <Loader />;

  if (!annonce)
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-4 lg:p-6">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Accord de confidentialité</h1>
            <p className="text-base text-gray-600 mb-4">Veuillez signer le NDA pour accéder aux informations confidentielles de l'annonce</p>
          </header>
          <button
            className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-dark transition-all duration-200 flex items-center space-x-2"
            onClick={() => navigate("/annonce")}>
            <span>Retourner aux annonces</span>
          </button>
        </div>
      </div>
    );

  // If NDA already exists, show success message with dataroom access
  if (existingNDA) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-4 lg:p-6">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Accord de confidentialité</h1>
            <p className="text-base text-gray-600 mb-4">Votre NDA a déjà été signé pour cette annonce</p>
          </header>

          {annonce && (
            <div className="flex flex-col gap-2 mb-6 bg-white rounded-lg border border-gray-200 p-3">
              <div className="flex items-center space-x-4">
                {annonce.annonce_image ? (
                  <img src={annonce.annonce_image} alt="Annonce" className="h-10 w-10 rounded object-cover border border-gray-200" />
                ) : (
                  <div className="h-10 w-10 flex items-center justify-center bg-blue-50 rounded text-blue-600 text-lg border border-blue-100">
                    <span>📄</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-medium text-blue-700 bg-blue-50 rounded px-2 py-0.5">{annonce.annonce_type}</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 truncate">{annonce.annonce_title}</div>
                  <div className="text-xs text-gray-500">{annonce.annonce_created_at && `Créée le ${new Date(annonce.annonce_created_at).toLocaleDateString("fr-FR")}`}</div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8 text-center space-y-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-gray-900">NDA signé avec succès</h2>
                <p className="text-gray-600 max-w-md mx-auto">
                  Votre accord de confidentialité a été signé{" "}
                  {existingNDA.nda_user_from_signed_at ? "le " + new Date(existingNDA.nda_user_from_signed_at).toLocaleDateString("fr-FR") : "récemment"}. Vous pouvez maintenant
                  accéder aux documents confidentiels de cette annonce.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/annonce")}
                  className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Retourner aux annonces</span>
                </button>
                <button
                  onClick={() => navigate("/dataroom")}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <span>Accéder à la dataroom</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 lg:p-6">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accord de confidentialité</h1>
          <p className="text-base text-gray-600 mb-4">Veuillez signer le NDA pour accéder aux informations confidentielles de l'annonce</p>
        </header>

        <div className="flex items-center justify-center space-x-3 mb-6">
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all duration-200 cursor-pointer ${
                currentStep >= 1 ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-300"
              }`}
              onClick={() => currentStep >= 1 && setCurrentStep(1)}>
              1
            </div>
            <span className={`ml-2 text-xs font-medium ${currentStep >= 1 ? "text-blue-600" : "text-gray-500"}`}>Informations</span>
          </div>
          <div className={`w-12 h-0.5 transition-all duration-200 cursor-pointer ${currentStep >= 2 ? "bg-blue-600" : "bg-gray-300"}`}></div>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all duration-200 ${
                currentStep >= 2 ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-300"
              }`}
              onClick={() => currentStep >= 2 && setCurrentStep(2)}>
              2
            </div>
            <span className={`ml-2 text-xs font-medium ${currentStep >= 2 ? "text-blue-600" : "text-gray-500"}`}>Accord</span>
          </div>
          <div className={`w-12 h-0.5 transition-all duration-200 ${currentStep >= 3 ? "bg-blue-600" : "bg-gray-300"}`}></div>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all duration-200 cursor-pointer ${
                currentStep >= 3 ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-300"
              }`}>
              3
            </div>
            <span className={`ml-2 text-xs font-medium ${currentStep >= 3 ? "text-blue-600" : "text-gray-500"}`}>Signature</span>
          </div>
        </div>

        {annonce && (
          <div className="flex flex-col gap-2 mb-4 bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center space-x-4">
              {annonce.annonce_image ? (
                <img src={annonce.annonce_image} alt="Annonce" className="h-10 w-10 rounded object-cover border border-gray-200" />
              ) : (
                <div className="h-10 w-10 flex items-center justify-center bg-blue-50 rounded text-blue-600 text-lg border border-blue-100">
                  <span>📄</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 rounded px-2 py-0.5">{annonce.annonce_type}</span>
                </div>
                <div className="text-sm font-semibold text-gray-900 truncate">{annonce.annonce_title}</div>
                <div className="text-xs text-gray-500">{annonce.annonce_created_at && `Créée le ${new Date(annonce.annonce_created_at).toLocaleDateString("fr-FR")}`}</div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {currentStep === 1 && (
            <div className="p-6 space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-xl font-bold text-gray-900">Informations de votre société</h2>
                <p className="text-gray-600 max-w-md mx-auto text-sm">Veuillez remplir les informations de votre entreprise pour personnaliser l'accord de confidentialité</p>
              </div>

              <div className="max-w-4xl mx-auto space-y-4">
                <div className="space-y-3 w-fit">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Qui représentez-vous dans cette mise en relation ? <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="entityType"
                    value={formData.entityType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200">
                    <option value="morale">Personne morale</option>
                    <option value="physique">Personne physique</option>
                  </select>
                </div>

                {formData.entityType === "morale" ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Nom de l'entreprise <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="Ex: Ma Société SARL"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Secteur de votre entreprise <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="sector"
                          value={formData.sector}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200">
                          <option value="">Choisir</option>
                          {sectorOptions.map((sector) => (
                            <option key={sector} value={sector}>
                              {sector}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Adresse postale de l'entreprise <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                        rows="2"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Nom du représentant <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="representativeName"
                          value={formData.representativeName}
                          onChange={handleInputChange}
                          placeholder="Ex: Jean Dupont"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Votre poste <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="representativeFunction"
                          value={formData.representativeFunction}
                          onChange={handleInputChange}
                          placeholder="Ex: Directeur Général"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="representativeName"
                        value={formData.representativeName}
                        onChange={handleInputChange}
                        placeholder="Ex: Jean Dupont"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Votre adresse postale <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                        rows="2"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!isFormValid}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2">
                    <span>Continuer</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="p-6 space-y-6">
              <div className="bg-gray-50 rounded-xl p-6 max-h-[400px] overflow-y-auto space-y-4 text-sm">
                <div className="text-center">
                  <h3 className="text-lg font-bold uppercase text-gray-900">ACCORD DE CONFIDENTIALITÉ</h3>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-base text-gray-900">ENTRE</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h5 className="font-medium text-blue-600">D'UNE PART</h5>
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <p className="font-semibold text-gray-900">LINKERA</p>
                        <p className="text-gray-700 text-sm">28 RUE JEAN-BAPTISTE PIGALLE, 75009 PARIS</p>
                        <p className="text-gray-700 text-sm">Représenté par Loïc Fleury, Président</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Habilité à représenter l'entreprise LINKERA dans le cadre des discussions relatives à l'annonce {annonce?.annonce_title || "cette annonce"}.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h5 className="font-medium text-blue-600">ET D'AUTRE PART</h5>
                      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <p className="font-semibold text-gray-900 text-sm">
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Votre nom"}
                        </p>
                        <p className="text-gray-700 text-sm">{formData.address || "Adresse"}</p>
                        {formData.entityType === "morale" && (
                          <p className="text-gray-700 text-sm">
                            Représenté par {formData.representativeName || "Nom du représentant"}, {formData.representativeFunction || "Fonction"}
                          </p>
                        )}
                        <p className="text-xs text-gray-600 mt-1">
                          {formData.entityType === "morale"
                            ? `Habilité à représenter l'entreprise ${formData.companyName || "votre société"} dans le cadre des discussions relatives à l'annonce ${
                                annonce?.annonce_title || "cette annonce"
                              }.`
                            : `Habilité à représenter ${formData.representativeName || "vous"} dans le cadre des discussions relatives à l'annonce ${
                                annonce?.annonce_title || "cette annonce"
                              }.`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 text-sm">
                    LINKERA et {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} seront ci-après dénommées
                    collectivement les "Parties" et individuellement une "Partie".
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-base underline text-gray-900">ETANT PREALABLEMENT RAPPELE QUE :</h4>
                  <div className="text-justify text-gray-700 space-y-2">
                    <p>
                      La société X ({annonce?.annonce_title || "cette annonce"}) souhaite se rapprocher d'une autre société (ci-après le "Projet"). À cet effet, les deux Parties
                      ont souhaité se rencontrer / échanger.
                    </p>
                    <p>
                      Dans ce cadre, il est nécessaire que LINKERA communique à{" "}
                      {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} certaines informations confidentielles,
                      telles que définies au paragraphe 1 ci-dessous.
                    </p>
                    <p>
                      Les Parties conviennent que ces informations confidentielles ne seront communiquées par LINKERA à{" "}
                      {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} que dans les conditions prévues dans le
                      présent accord de confidentialité (ci-après « l'Accord »).
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-base underline text-gray-900">IL A ETE CONVENU CE QUI SUIT :</h4>

                  <div className="space-y-3">
                    <div>
                      <h5 className="font-medium underline text-gray-900">1. Définitions</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>
                          <strong>« L'Accord »</strong> désigne le présent accord de confidentialité.
                        </p>
                        <p>
                          <strong>"Information Confidentielle" ou "Information"</strong> désigne :
                        </p>
                        <div className="ml-4 space-y-1">
                          <p>
                            a) toute information ou idée, écrite ou orale, quelle que soit sa forme ou sa nature, qui a été ou sera communiquée par LINKERA à{" "}
                            {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"}, relative au Projet ;
                          </p>
                          <p>b) toute étude, tout document, toute conclusion, écrite ou orale, élaboré par LINKERA à partir de toute information visée au a) ci-dessus.</p>
                        </div>
                        <p>
                          <strong>Ne sont pas considérées comme informations confidentielles :</strong>
                        </p>
                        <div className="ml-4 space-y-1">
                          <p>
                            a) les informations déjà accessibles au public au moment de leur communication, ou qui viendraient à l'être autrement que par violation du présent
                            Accord ;
                          </p>
                          <p>
                            b) les informations déjà connues de la Partie réceptrice avant leur communication, à condition que cette Partie puisse en apporter la preuve écrite ;
                          </p>
                          <p>c) les informations obtenues de manière légitime auprès d'un tiers non tenu à une obligation de confidentialité.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">2. Confidentialité et utilisation restreinte des informations confidentielles</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} s'engage à garder strictement
                          confidentielles les Informations reçues et à ne les révéler à aucun tiers à quelque titre que ce soit.{" "}
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} ne sera autorisé à communiquer les
                          informations confidentielles qu'à ses employés ou aux conseils qui devront avoir connaissance de ces Informations pour les besoins du Projet et qui seront
                          informés du caractère confidentiel de ces Informations.
                        </p>
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} s'engage à n'utiliser aucune
                          informations confidentielles à toute autre fin que celle du Projet, et notamment à ne pas les utiliser à des fins concurrentielles.
                        </p>
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} s'engage enfin à prendre toutes
                          mesures nécessaires afin de protéger la confidentialité des Informations et d'éviter leur utilisation non autorisée, leur divulgation, publication ou
                          communication.
                        </p>
                        <p>
                          Si {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} est tenu de révéler une
                          informations confidentielles à une quelconque autorité administrative ou judiciaire française, communautaire ou étrangère, il s'engage à en avertir
                          préalablement LINKERA, dans un délai raisonnable, de la date et de la teneur de l'Information à communiquer. Le contenu de l'Information sera défini d'un
                          commun accord entre les Parties.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">3. Restitution et destruction des informations confidentielles</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>
                          Sur simple demande de LINKERA, {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"}{" "}
                          s'engage à restituer sans délai, ou à détruire à sa demande, tout document, note, fichier, code d'accès ou élément (ainsi que toute copie) contenant les
                          informations confidentielles communiquées par LINKERA.
                        </p>
                        <p>Cela inclut notamment :</p>
                        <div className="ml-4 space-y-1">
                          <p>- les supports papiers et électroniques ;</p>
                          <p>
                            - les copies numériques, y compris celles présentes dans des systèmes informatiques, emails ou disques de sauvegarde, dans la mesure du raisonnablement
                            accessible ;
                          </p>
                          <p>- tout élément élaboré à partir des informations confidentielles.</p>
                        </div>
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} confirmera par écrit la restitution
                          ou destruction.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">4. Non-sollicitation</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} s'engage, pendant une durée de 24
                          mois à compter de la signature du présent Accord, à ne pas solliciter ni recruter, directement ou indirectement, les salariés, clients, fournisseurs,
                          sous-traitants ou partenaires stratégiques de la société X, sauf accord écrit préalable de LINKERA.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">5. Indemnisation</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>
                          En cas de manquement de {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} à l'une des
                          obligations qui les incombent en vertu du présent engagement et dans le cas où LINKERA ainsi que la société X subiraient un préjudice, nonobstant le
                          renvoi des Informations Confidentielles, en raison des connaissances qui seraient irrémédiablement acquises par{" "}
                          {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} et/ou des tiers,{" "}
                          {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} s'engage à indemniser totalement de
                          ce préjudice.
                        </p>
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} reconnaît que les actionnaires de la
                          société X se réservent le droit, à leur seule initiative à tout moment sans notification préalable, de ne donner suite à aucun contact engagé, et{" "}
                          {formData.entityType === "morale" ? formData.companyName || "votre société" : formData.representativeName || "vous"} reconnaît qu'aucune indemnité de
                          quelque nature que ce soit ne pourra être réclamée par eux-mêmes à ce titre, ni aux actionnaires de la société X, ni à leurs conseils.
                        </p>
                        <p>
                          {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Vous"} reconnaît enfin que ni la société X,
                          ni les actionnaires, ni leurs conseils ne peuvent garantir l'exactitude ou le caractère exhaustif des Informations, même si celles-ci sont communiquées de
                          bonne foi et conformément aux usages en la matière.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">6. Durée</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>Les engagements contenus dans le présent Accord resteront en vigueur pendant une période de deux (2) ans à compter de la signature.</p>
                        <p>La date d'entrée en vigueur est fixée au {formData.date}.</p>
                        <p>La fin de l'Accord ne dégage pas les Parties de leurs obligations de confidentialité quant aux informations échangées pendant leur coopération.</p>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium underline text-gray-900">7. Divers – Loi applicable – Juridiction compétente</h5>
                      <div className="ml-4 space-y-2 text-gray-700">
                        <p>Le présent Accord est soumis au droit français. Tout litige relatif à l'interprétation ou à l'application sera soumis aux Tribunaux de Paris.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Retour</span>
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2">
                  <span>Continuer vers la signature</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="p-6 space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-xl font-bold text-gray-900">Signature de l'accord</h2>
                <p className="text-gray-600 max-w-md mx-auto text-sm">Finalisez la signature de votre accord de confidentialité</p>
              </div>

              <div className="max-w-4xl mx-auto space-y-6">
                <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-base text-gray-900">Détails d'exécution</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Ville <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Ex: Paris"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="text-center text-sm text-gray-600 bg-white p-3 rounded-lg border border-gray-200">
                    Fait à {formData.city}, le {formData.date}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
                    <p className="font-semibold text-gray-900 text-base">La société Linkera</p>
                    <p className="text-gray-700 text-sm">Représenté par Loic Fleury</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
                    <p className="font-semibold text-gray-900 text-base">
                      {formData.entityType === "morale" ? formData.companyName || "Votre société" : formData.representativeName || "Votre nom"}
                    </p>
                    {formData.entityType === "morale" && <p className="text-gray-700 text-sm">Représenté par {formData.representativeName || "Nom du représentant"}</p>}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="accept"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="accept" className="text-sm text-gray-700 leading-relaxed">
                        <strong>Je déclare avoir lu et approuvé les termes du présent accord de confidentialité.</strong>
                        <br />
                        Je m'engage à respecter toutes les obligations qui en découlent et à maintenir la confidentialité des informations reçues.
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span>Retour</span>
                    </button>
                    <div className="flex items-end space-x-2">
                      <button
                        onClick={handlePrintNDA}
                        disabled={!accepted || !formData.city.length || !formData.date || printing}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2">
                        {printing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Génération en cours...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            <span>Consulter le NDA</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!accepted || signing || !formData.city.length}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2">
                        {signing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Signature en cours...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            <span>Signer le NDA</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NDA;
